/**
 * ConfirmDialog - Reusable confirmation modal
 *
 * Replaces native confirm() with a styled modal that matches the app's design.
 * Supports destructive actions with visual warning styling.
 *
 * Issue #1029
 */

import { useEffect, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';
import Button from './Button';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';
  /**
   * While the caller's action is in flight, locks the confirm button (spinner
   * + disabled, via Button's `loading` idiom) so a double-click can't dispatch
   * the mutation twice (#3440), AND gates every dismiss path — the cancel
   * button is disabled and backdrop-click / Escape are inert — so the user
   * can't dismiss mid-request, believe they aborted, and have the action
   * complete behind them. Wire it from the caller's mutation `isPending`.
   * Mirrors the native ConfirmDialog's `confirmLoading`
   * (mobile/src/components/ui/ConfirmDialog.tsx; George round 3 on #3265).
   */
  isPending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  isPending = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);

  // One-shot confirm latch (#3440). `isPending` disables the confirm Button,
  // but `disabled` only takes effect after React commits, and close-on-confirm
  // unmounts after the tick — so a SAME-TICK second confirm (two clicks before
  // the re-render) would still reach onConfirm. firedRef blocks that in the
  // same tick, before any state has propagated. Reset rule: re-arm when the
  // dialog (re)opens AND after a request completes with the dialog still open
  // (isPending back to false), so a retry on a dialog kept open by a failed
  // request (e.g. ClubSettingsInvitesPage onError, which does not close) works.
  const firedRef = useRef(false);
  useEffect(() => {
    if (isOpen && !isPending) firedRef.current = false;
  }, [isOpen, isPending]);

  // firedRef is the sole re-entry guard: the `loading={isPending}` Button is
  // already disabled across-task, so an `isPending` check here would be dead
  // (handleConfirm is only reachable while the button is enabled) — RED shows
  // it fails no test. One mechanism, no belt (feedback_belt_and_suspenders).
  const handleConfirm = () => {
    if (firedRef.current) return;
    firedRef.current = true;
    onConfirm();
  };

  // Close on escape key — inert while pending so a mid-request Escape can't
  // dismiss a dialog whose action still completes (mirrors native's gate).
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isPending) {
        onCancel();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isPending, onCancel]);

  // Focus trap and initial focus
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      const firstButton = dialogRef.current.querySelector('button');
      firstButton?.focus();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        // Backdrop dismiss is inert while pending (mirrors native's gate).
        if (e.target === e.currentTarget && !isPending) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-busy={isPending}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-message"
        className="bg-oro-surface border border-oro-border rounded-lg max-w-sm w-full p-6 shadow-card animate-in fade-in zoom-in-95 duration-150"
      >
        <div className="flex items-start gap-4">
          {variant === 'destructive' && (
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-oro-error-soft flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-oro-error" />
            </div>
          )}
          <div className="flex-1">
            <h3
              id="confirm-dialog-title"
              className="text-lg font-semibold text-text-primary mb-2"
            >
              {title}
            </h3>
            <p
              id="confirm-dialog-message"
              className="text-text-secondary text-sm"
            >
              {message}
            </p>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            variant="secondary"
            className="flex-1"
            disabled={isPending}
            onClick={onCancel}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={variant === 'destructive' ? 'destructive' : 'primary'}
            className="flex-1"
            loading={isPending}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
